import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertCircle, 
  X, 
  RefreshCw,
  Download,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { 
  BulkHistoricalUploadService, 
  FileMapping, 
  type ParsedFile 
} from '@/services/bulk-historical-upload-service';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface BulkHistoricalUploadProps {
  isOpen: boolean;
  onClose: () => void;
  parentBotId: string;
  availableBots: Array<{ id: string; name: string; mapping_id?: string }>;
  onUploadComplete: () => void;
}

export const BulkHistoricalUpload: React.FC<BulkHistoricalUploadProps> = ({
  isOpen,
  onClose,
  parentBotId,
  availableBots,
  onUploadComplete
}) => {
  const [fileMappings, setFileMappings] = useState<FileMapping[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState<string>('');
  const queryClient = useQueryClient();

  // Helper function to normalize instrument names
  const normalizeInstrument = (instrument: string): string => {
    return instrument.trim().toUpperCase().replace(/\s+/g, '');
  };

  // File drop handler
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setIsProcessing(true);
    try {
      const mappings = await BulkHistoricalUploadService.processFiles(acceptedFiles, availableBots);
      setFileMappings(mappings);
      toast.success(`Processed ${mappings.length} files with auto-mapping suggestions`);
    } catch (error) {
      console.error('Error processing files:', error);
      toast.error('Failed to process files');
    } finally {
      setIsProcessing(false);
    }
  }, [availableBots]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: true
  });

  // Update mapping configuration
  const updateMapping = (index: number, updates: Partial<FileMapping>) => {
    setFileMappings(prev => 
      prev.map((mapping, i) => i === index ? { ...mapping, ...updates } : mapping)
    );
  };

  // Bulk actions
  const enableAll = () => {
    setFileMappings(prev => prev.map(m => ({ ...m, enabled: !!m.suggestedBot })));
  };

  const disableAll = () => {
    setFileMappings(prev => prev.map(m => ({ ...m, enabled: false })));
  };

  const autoFillAll = () => {
    setFileMappings(prev => prev.map(m => ({
      ...m,
      selectedBotId: m.suggestedBot?.botId,
      enabled: !!m.suggestedBot && m.suggestedBot.confidence >= 50
    })));
  };

  // Upload selected files
  const handleBulkUpload = async () => {
    const enabledMappings = fileMappings.filter(m => m.enabled && m.selectedBotId);
    
    if (enabledMappings.length === 0) {
      toast.error('No files selected for upload');
      return;
    }

    setIsProcessing(true);
    setUploadProgress(0);

    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (let i = 0; i < enabledMappings.length; i++) {
      const mapping = enabledMappings[i];
      setCurrentFile(mapping.file.file.name);
      setUploadProgress((i / enabledMappings.length) * 100);

      try {
        // Find mapping_id for the selected bot
        const botMapping = availableBots.find(bot => bot.id === mapping.selectedBotId);
        if (!botMapping?.mapping_id) {
          throw new Error('Bot mapping not found');
        }

        // Determine instrument and configuration
        let instrument = '';
        let tpUsd = null;
        let slUsd = null;
        let quantity = null;

        if (mapping.file.detectedConfig) {
          // Use detected configuration from CSV Analysis
          instrument = normalizeInstrument(mapping.file.detectedConfig.instrument);
          tpUsd = mapping.file.detectedConfig.tp_usd;
          slUsd = mapping.file.detectedConfig.sl_usd;
          quantity = mapping.file.detectedConfig.quantity;
        } else {
          // Extract from filename
          instrument = normalizeInstrument(mapping.file.parsedName.symbol);
          if (mapping.file.parsedName.tp) tpUsd = mapping.file.parsedName.tp;
          if (mapping.file.parsedName.sl) slUsd = mapping.file.parsedName.sl;
        }

        // Upload file data
        const { error: fileError } = await supabase
          .from('bot_merge_history_files')
          .upsert({
            mapping_id: botMapping.mapping_id,
            bot_id: mapping.selectedBotId,
            filename: mapping.file.file.name,
            file_data: mapping.file.csvData,
            file_size: mapping.file.file.size,
            instrument: instrument || null,
          }, {
            onConflict: 'mapping_id,bot_id'
          });

        if (fileError) throw fileError;

        // Update/insert TP/SL settings if detected from CSV Analysis
        if (mapping.file.detectedConfig && (tpUsd !== null || slUsd !== null || quantity !== null)) {
          const { error: settingsError } = await supabase
            .from('bot_merge_analysis_settings')
            .upsert({
              parent_bot_id: parentBotId,
              child_bot_id: mapping.selectedBotId,
              tp_usd: tpUsd,
              sl_usd: slUsd,
              quantity: quantity,
              created_by: (await supabase.auth.getUser()).data.user?.id
            }, {
              onConflict: 'parent_bot_id,child_bot_id'
            });

          if (settingsError) {
            console.warn('Error updating settings:', settingsError);
          }
        }

        successCount++;
      } catch (error) {
        console.error(`Error uploading ${mapping.file.file.name}:`, error);
        errorCount++;
        errors.push(`${mapping.file.file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    setUploadProgress(100);
    setCurrentFile('');

    // Show results
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
    }
    
    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} file${errorCount > 1 ? 's' : ''}`, {
        description: errors.slice(0, 3).join(', ') + (errors.length > 3 ? '...' : '')
      });
    }

    // Refresh data and close
    queryClient.invalidateQueries({ queryKey: ['child-bots-with-data'] });
    onUploadComplete();
    
    if (successCount > 0) {
      setFileMappings([]);
      if (errorCount === 0) {
        onClose();
      }
    }
    
    setIsProcessing(false);
  };

  // Clear all files
  const clearFiles = () => {
    setFileMappings([]);
  };

  // Export configuration as template
  const exportConfig = () => {
    const config = fileMappings.map(m => ({
      filename: m.file.file.name,
      suggestedBot: m.suggestedBot?.botName,
      confidence: m.suggestedBot?.confidence,
      enabled: m.enabled,
      parsedSymbol: m.file.parsedName.symbol,
      parsedTP: m.file.parsedName.tp,
      parsedSL: m.file.parsedName.sl,
      detectedConfig: m.file.detectedConfig
    }));

    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-upload-config-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Configuration exported');
  };

  const enabledCount = fileMappings.filter(m => m.enabled).length;
  const highConfidenceCount = fileMappings.filter(m => m.suggestedBot && m.suggestedBot.confidence >= 70).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-none md:w-[66vw] md:h-[85vh] max-h-[90vh] overflow-y-auto flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Bulk Historical File Upload</DialogTitle>
          <DialogDescription>
            Upload multiple CSV/Excel files with automatic bot mapping based on filenames
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* File Drop Zone */}
          <Card>
            <CardContent className="pt-6">
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">
                  {isDragActive ? 'Drop files here...' : 'Drop files or click to browse'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Supports CSV and Excel files (.csv, .xlsx, .xls)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {fileMappings.length > 0 && (
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{fileMappings.length}</div>
                  <p className="text-sm text-muted-foreground">Total Files</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-600">{highConfidenceCount}</div>
                  <p className="text-sm text-muted-foreground">High Confidence</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-blue-600">{enabledCount}</div>
                  <p className="text-sm text-muted-foreground">Enabled</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-purple-600">
                    {fileMappings.filter(m => m.file.detectedConfig).length}
                  </div>
                  <p className="text-sm text-muted-foreground">Auto-Config</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Bulk Actions */}
          {fileMappings.length > 0 && (
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-2">
                  <Button onClick={autoFillAll} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Auto-fill All
                  </Button>
                  <Button onClick={enableAll} variant="outline" size="sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Enable All
                  </Button>
                  <Button onClick={disableAll} variant="outline" size="sm">
                    Disable All
                  </Button>
                  <Button onClick={exportConfig} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export Config
                  </Button>
                  <Button onClick={clearFiles} variant="outline" size="sm">
                    <X className="h-4 w-4 mr-2" />
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* File Mapping Table */}
          {fileMappings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  File Mappings ({enabledCount} enabled)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-[50vh] overflow-y-auto border rounded-md p-2 scrollbar-thin scrollbar-thumb-border scrollbar-track-background">
                  {fileMappings.map((mapping, index) => {
                    const confidence = BulkHistoricalUploadService.getConfidenceDisplay(
                      mapping.suggestedBot?.confidence || 0
                    );

                    return (
                      <div 
                        key={index} 
                        className={`border rounded-lg p-4 space-y-3 ${
                          mapping.enabled ? 'border-primary/50 bg-primary/5' : 'border-border'
                        }`}
                      >
                        {/* File Info Row */}
                        <div className="flex items-center gap-4">
                          <Checkbox
                            checked={mapping.enabled}
                            onCheckedChange={(checked) => 
                              updateMapping(index, { enabled: !!checked })
                            }
                          />
                          <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                          <div className="flex-1">
                            <p className="font-medium">{mapping.file.file.name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                               <span>Symbol: {mapping.file.parsedName.symbol || 'Unknown'}</span>
                               {mapping.file.parsedName.risk && (
                                 <span>• Risk: {mapping.file.parsedName.risk}</span>
                               )}
                               {mapping.file.parsedName.timeframe && (
                                 <span>• TF: {mapping.file.parsedName.timeframe}</span>
                               )}
                               {mapping.file.parsedName.tp && (
                                 <span>• TP: ${mapping.file.parsedName.tp}</span>
                               )}
                               {mapping.file.parsedName.sl && (
                                 <span>• SL: ${mapping.file.parsedName.sl}</span>
                               )}
                               {mapping.file.detectedConfig && (
                                 <Badge variant="secondary" className="text-xs">
                                   Auto-Config
                                 </Badge>
                               )}
                               {/* Show mismatch warning */}
                               {mapping.suggestedBot && (
                                 (() => {
                                   const fileRisk = mapping.file.parsedName.risk;
                                   const fileTF = mapping.file.parsedName.timeframe;
                                   const botRisk = mapping.suggestedBot.parsedBot.risk;
                                   const botTF = mapping.suggestedBot.parsedBot.timeframe;
                                   const hasTFMismatch = fileTF && botTF && fileTF.toUpperCase() !== botTF.toUpperCase();
                                   const hasRiskMismatch = fileRisk && botRisk && Math.abs(parseFloat(fileRisk) - parseFloat(botRisk)) > 0.1;
                                   
                                   if (hasTFMismatch || hasRiskMismatch) {
                                     return (
                                       <Badge variant="destructive" className="text-xs">
                                         Mismatch: {hasTFMismatch ? 'TF' : ''}{hasTFMismatch && hasRiskMismatch ? '/' : ''}{hasRiskMismatch ? 'Risk' : ''}
                                       </Badge>
                                     );
                                   }
                                   return null;
                                 })()
                               )}
                             </div>
                          </div>
                          {mapping.file.error && (
                            <AlertCircle className="h-5 w-5 text-red-500" />
                          )}
                        </div>

                        {/* Bot Selection Row */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm">Bot Selection</Label>
                            <Select
                              value={mapping.selectedBotId || ''}
                              onValueChange={(value) => 
                                updateMapping(index, { selectedBotId: value })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select bot..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableBots.map(bot => (
                                  <SelectItem key={bot.id} value={bot.id}>
                                    {bot.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm">Auto-Match Confidence</Label>
                            <div className="flex items-center gap-2 mt-2">
                              {mapping.suggestedBot ? (
                                <>
                                  <Badge variant="outline" className={confidence.color}>
                                    {mapping.suggestedBot.confidence}% - {confidence.label}
                                  </Badge>
                                  <span className="text-sm text-muted-foreground">
                                    → {mapping.suggestedBot.botName}
                                  </span>
                                </>
                              ) : (
                                <span className="text-sm text-muted-foreground">No match found</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Configuration Row */}
                        {mapping.file.detectedConfig && (
                          <div className="bg-muted/50 rounded p-3">
                            <p className="text-sm font-medium mb-2">Detected Configuration:</p>
                            <div className="grid grid-cols-4 gap-2 text-sm">
                              <span>Instrument: {mapping.file.detectedConfig.instrument || 'N/A'}</span>
                              <span>TP: ${mapping.file.detectedConfig.tp_usd ?? 'N/A'}</span>
                              <span>SL: ${mapping.file.detectedConfig.sl_usd ?? 'N/A'}</span>
                              <span>Qty: {mapping.file.detectedConfig.quantity ?? 'N/A'}</span>
                            </div>
                          </div>
                        )}

                        {/* Error Display */}
                        {mapping.file.error && (
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-sm text-red-600">{mapping.file.error}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upload Progress */}
          {isProcessing && fileMappings.length > 0 && (
            <Card>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Uploading files...</span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <Progress value={uploadProgress} className="w-full" />
                  {currentFile && (
                    <p className="text-sm text-muted-foreground">
                      Processing: {currentFile}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpload}
              disabled={enabledCount === 0 || isProcessing}
            >
              Upload {enabledCount} File{enabledCount !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};